import type { Guest } from "./hosTypes";
import { getLocalTodayIso, isValidIsoDate } from "./dateValidation.ts";

export type MandatoryGuestField =
  | "firstName"
  | "lastName"
  | "dateOfBirth"
  | "nationality"
  | "address";

export type MandatoryGuestInfo = Pick<
  Guest,
  "firstName" | "lastName" | "dateOfBirth" | "nationality" | "address"
>;

export type MandatoryGuestErrors = Partial<Record<MandatoryGuestField, string>>;

export const mandatoryGuestFieldLabels: Record<MandatoryGuestField, string> = {
  firstName: "First name",
  lastName: "Last name",
  dateOfBirth: "Date of birth",
  nationality: "Nationality",
  address: "Address",
};

export function normalizeMandatoryGuestInfo<T extends Partial<MandatoryGuestInfo>>(input: T): T {
  return {
    ...input,
    firstName: input.firstName?.trim(),
    lastName: input.lastName?.trim(),
    dateOfBirth: input.dateOfBirth?.trim(),
    nationality: input.nationality?.trim(),
    address: input.address?.trim(),
  };
}

function isValidPastOrPresentDate(value: string) {
  if (!isValidIsoDate(value)) return false;
  return value <= getLocalTodayIso();
}

export function validateMandatoryGuestInfo(input: Partial<MandatoryGuestInfo>): MandatoryGuestErrors {
  const guest = normalizeMandatoryGuestInfo(input);
  const errors: MandatoryGuestErrors = {};

  if (!guest.firstName) errors.firstName = "First name is required.";
  if (!guest.lastName) errors.lastName = "Last name is required.";
  if (!guest.dateOfBirth) {
    errors.dateOfBirth = "Date of birth is required.";
  } else if (!isValidPastOrPresentDate(guest.dateOfBirth)) {
    errors.dateOfBirth = "Enter a valid date of birth that is not in the future.";
  }
  if (!guest.nationality) errors.nationality = "Nationality is required.";
  if (!guest.address) errors.address = "Address is required.";

  return errors;
}

export function hasMandatoryGuestErrors(errors: MandatoryGuestErrors) {
  return Object.keys(errors).length > 0;
}
